import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import { AppUtils, SignolUtils, StorageUtils } from "@src/utils";
import {
  signolImagesRepositoryURL,
  IDP_LOGIN_URL,
  SSO_LOGIN_ATTEMPT_COOKIE,
  UsernameTypes,
} from "@src/constants/constants";

import Form from "@src/components/forms/Form";
import FormInput from "@src/components/atoms/FormInput";
import Button from "@src/components/atoms/Button";
import { ImageWithRetry, Loader } from "@src/components/atoms";
import { loginEmailSchema, loginUsernameSchema } from "./formHelpers";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const envType = import.meta.env.VITE_REACT_APP_ENV;

const LoginForm = ({
  title,
  usernameType,
  onLoginSubmit,
  defaultValues,
  logo,
  validationMode = null, //pass validationMode='onChange' here is it needs to validate on keystroke change
}) => {
  const navigateTo = useNavigate();
  const { search } = useLocation();
  const isFromSendgrid =
    new URLSearchParams(search).get("utm_source") === "sendgrid";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isSSOLogin, setIsSSOLogin] = useState(false);

  const isUsername = usernameType === UsernameTypes.USERNAME;
  const usernameInfo = isUsername
    ? "This is the unique ID you used to register e.g. discharge book number, employee ID, or username."
    : "";

  const checkTrustedDomain = (e) => {
    const email = e.target.value;
    const ssoCustomer = SignolUtils.getLoginSSO(email);
    if (ssoCustomer) {
      setIsSSOLogin(true);
      StorageUtils.setItem(SSO_LOGIN_ATTEMPT_COOKIE, email);
      // display loader for 2s and then redirect to external IdP login screen
      setTimeout(() => {
        console.log("redirecting to SSO external provider...");
        window.location.href = IDP_LOGIN_URL(ssoCustomer);
      }, 1500);
    }
  };

  const handleSubmit = async (data) => {
    setError("");
    setIsSubmitting(true);
    try {
      await onLoginSubmit(data);
    } catch (error) {
      setError(
        error?.error ||
          "Failed to log in. Please check your details and try again.",
      );
      console.error("Login error", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white">
      <div className="mx-auto py-8 w-40">
        <ImageWithRetry
          src={
            !!logo
              ? `${signolImagesRepositoryURL}/customers/${logo}/logo_${logo}_customer.png`
              : `${signolImagesRepositoryURL}/SignolLogoPrimary.png`
          }
          alt="Logo"
        />
        {envType !== "production" && (
          <span className="relative bottom-3 float-right font-bold text-pink-400">
            {envType === "dev" ? "qa" : envType}
          </span>
        )}
      </div>

      {title && <h3 className="mb-6 text-center">{title}</h3>}
      {usernameType === UsernameTypes.GUEST ? (
        // Industry Select
        <>
          <Button onClick={() => navigateTo(`/loginEmail`)}>Aviation</Button>
          <Button
            onClick={() =>
              navigateTo(
                isFromSendgrid
                  ? `/loginUsername?utm_source=sendgrid`
                  : `/loginUsername`,
              )
            }
          >
            Maritime
          </Button>
        </>
      ) : (
        // Username/Password form
        <>
          <Form
            onSubmit={handleSubmit}
            schema={isUsername ? loginUsernameSchema : loginEmailSchema}
            options={{
              defaultValues: {
                username: defaultValues?.username ?? "",
              },
              mode: validationMode ? validationMode : null,
            }}
            className="space-y-6"
          >
            {(methods) => {
              return (
                <>
                  <FormInput
                    info={usernameInfo}
                    label={isUsername ? "Username" : "Email address"}
                    name="username"
                    type={isUsername ? "text" : "email"}
                    placeholder={isUsername ? "" : "example@company.com"}
                    error={methods.formState.errors.username?.message}
                    textColor={"primary"}
                    onChange={checkTrustedDomain}
                    validationMode={validationMode ? validationMode : null}
                    autoComplete="username"
                  />
                  {!!isSSOLogin ? (
                    <div className="block text-center mt-10 mb-32">
                      <p className="font-bold mb-5">
                        Please wait... <br /> We are redirecting to your SSO
                        provider
                      </p>
                      <Loader size="lg" />
                    </div>
                  ) : (
                    <>
                      <FormInput
                        label="Password"
                        name="password"
                        type="password"
                        placeholder="••••••••"
                        textColor={"primary"}
                        error={methods.formState.errors.password?.message}
                        autoComplete="current-password"
                      />
                      <div className="text-primary-500 text-left text-sm underline mt-4">
                        <Link
                          to={`/forgotPassword?ulabel=${isUsername ? "username" : "email"}`}
                          className="text-primary-500 hover:font-bold"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <div className="flex justify-center">
                        <div className="flex">
                          <Button submit loading={isSubmitting}>
                            {" "}
                            <div className="px-4">Log in</div>
                          </Button>
                        </div>
                      </div>
                      {isUsername && (
                        <p className="text-sm text-center text-neutral-500 mt-2">
                          New to the vessel?{" "}
                          <a
                            href="/crewUpdate"
                            className="underline text-primary-500 hover:text-primary-700"
                          >
                            Let us know
                          </a>
                        </p>
                      )}
                    </>
                  )}
                  {error && (
                    <div className="mt-4 text-xs italic text-red-500">
                      {error}
                    </div>
                  )}
                </>
              );
            }}
          </Form>
          {/* TODO this is being removed while we address inconsistencies in UX flow 
          isUsername && (
            <div className="flex items-center justify-start py-6 text-sm text-gray-500">
              Don't have an account?
              <div className="text-primary-500 text-center underline mx-2">
                <Link
                  to={signupLink}
                  className="text-primary-500 hover:font-bold"
                >
                  Sign up
                </Link>
              </div>
            </div>
          )*/}
        </>
      )}
    </div>
  );
};

export const loginFormControls = {
  title: {
    propType: PropTypes.string,
    controlType: "text",
  },
  usernameType: {
    propType: PropTypes.oneOf(Object.values(UsernameTypes)).isRequired,
    description: "Type of login: Guest, Email Address, Username",
    controlType: "select",
    options: Object.values(UsernameTypes),
  },
  logo: {
    ...COMMON_CONTROLS.customerCode,
    options: [undefined, ...COMMON_CONTROLS.customerCode.options],
  },
  onLoginSubmit: {
    propType: PropTypes.func.isRequired,
    description: "Form submission handler from parent",
    disable: true,
  },
  validationMode: {
    propType: PropTypes.string,
    controlType: "select",
    options: [undefined, "onChange"],
  },
};

LoginForm.propTypes = AppUtils.getPropTypeFromControls(loginFormControls);

export default LoginForm;
